import { useIntl } from 'react-intl';

import messages from './messages';

const SECONDS_IN_MIN = 60;
const SECONDS_IN_HR = SECONDS_IN_MIN * 60;
const SECONDS_IN_DAY = SECONDS_IN_HR * 24;
const SECONDS_IN_WEEK = SECONDS_IN_DAY * 7;
const SECONDS_IN_MONTH = SECONDS_IN_DAY * 30;

export const getCurrentOrigin = (): string | undefined => {
	if (typeof window === 'undefined') {
		return;
	}
	try {
		return window.origin;
	} catch {
		return undefined;
	}
};

export const useDateParser = (isoDate: string): string => {
	const { formatDate, formatTime } = useIntl();

	const date = formatDate(isoDate, { day: '2-digit', month: '2-digit', year: 'numeric' });
	const time = formatTime(isoDate, { hour: '2-digit', hour12: true, minute: 'numeric' });
	return `${date} ${time}`;
};

export type UpdateInterval = 'm' | 'h' | 'stop';

export const useDisplayTime = ({
	isoDate,
	relativeTimeStyle = 'narrow',
	useAbbreviation = false,
}: {
	isoDate?: string;
	relativeTimeStyle?: 'long' | 'short' | 'narrow';
	useAbbreviation?: boolean;
}): { updateInterval: UpdateInterval; value?: string } => {
	const { formatMessage, formatRelativeTime } = useIntl();
	const parsedDate = useDateParser(isoDate || ''); // Call hook at top level

	// Helper to format relative time
	const formatRelativeTimeValue = (value: number, unit: Intl.RelativeTimeFormatUnit) => {
		return formatRelativeTime(useAbbreviation ? value : -value, unit, {
			style: relativeTimeStyle,
		});
	};

	if (!isoDate) {
		return {
			updateInterval: 'stop',
		};
	}

	const secondsSinceUpdate = Math.floor((Date.now() - new Date(isoDate).getTime()) / 1000);

	const totalMinutes = Math.floor(secondsSinceUpdate / SECONDS_IN_MIN);
	const totalHours = Math.floor(secondsSinceUpdate / SECONDS_IN_HR);
	const totalDays = Math.floor(secondsSinceUpdate / SECONDS_IN_DAY);
	const totalWeeks = Math.floor(secondsSinceUpdate / SECONDS_IN_WEEK);
	const totalMonths = Math.floor(secondsSinceUpdate / SECONDS_IN_MONTH);

	// relative time if less than 1 day
	if (totalHours <= 24) {
		if (totalMinutes < 1) {
			return {
				updateInterval: 'm',
				value: formatMessage(messages.now),
			};
		}
		if (totalMinutes >= 1 && totalMinutes < 60) {
			return {
				updateInterval: 'm',
				value: formatRelativeTimeValue(totalMinutes, 'minute'),
			};
		}

		if (totalHours >= 1 && totalHours < 24) {
			return {
				updateInterval: 'h',
				value: formatRelativeTimeValue(totalHours, 'hour'),
			};
		}
	}

	// Yesterday
	if (totalDays === 1) {
		return {
			updateInterval: 'stop',
			value: formatMessage(messages.yesterday),
		};
	}

	// X days ago (2-6 days)
	if (totalDays >= 2 && totalDays < 7) {
		return {
			updateInterval: 'stop',
			value: formatRelativeTimeValue(totalDays, 'day'),
		};
	}

	// X weeks ago
	if (totalDays >= 7 && totalDays < 30) {
		return {
			updateInterval: 'stop',
			value: formatRelativeTimeValue(totalWeeks, 'week'),
		};
	}

	// X months ago
	if (totalDays >= 30 && totalDays < 365) {
		return {
			updateInterval: 'stop',
			value: formatRelativeTimeValue(totalMonths, 'month'),
		};
	}

	// date timestamp
	return {
		updateInterval: 'stop',
		value: parsedDate,
	};
};
